import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin, Observable, of } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';
import { ConfirmationService } from '../../../../shared/services/confirmation.service';
import { AccountsReceivableService } from '../../../../services/accounts-receivable.service';
import { AccountsReceivable, AccountsReceivableUpdate, ReceiptPayment, AccountsReceivablePayment, CancelPaymentsBatchResponse } from '../../../../models/AccountsReceivable';
import { Customer } from '../../../../models/Customer';
import { PaymentService } from '../../../../services/payment.service';
import { CustomersService } from '../../../../services/customers.service';
import { AsaasCharge, AsaasCustomer, AsaasPaymentResponse, BBBoleto, BBPix, CancelBoletosBatchResponse } from '../../../../models/Payment';
import { ErrorService } from '../../../../shared/services/error.service';
import { CashflowService } from '../../../../services/cashflow.service';
import { AuthService } from '../../../../services/auth.service';
import { ServiceOrderReceivableSyncService } from '../../../../services/service-order-receivable-sync.service';
import {
  canReceiveAccountsReceivable,
  getReceiveBlockedReason,
  hasActiveReceivableCharge
} from '../../../../utils/accounts-receivable.utils';

@Component({
  selector: 'app-accounts-receivable-view',
  templateUrl: './accounts-receivable-view.component.html',
  styleUrls: ['./accounts-receivable-view.component.css']
})
export class AccountsReceivableViewComponent implements OnInit {
  accountsReceivable: AccountsReceivable | null = null;
  accountsReceivableId?: number;
  loading = false;
  paymentLoading = false;
  checkingPaymentStatus = false;
  cancellingCharge = false;
  asaasChargeStatus: string | null = null;
  asaasChargeId: string | null = null;
  /** Detalhes da cobrança Asaas (GET /payments/asaas/charges/{id}) — boleto/PIX. */
  asaasChargeDetails: AsaasPaymentResponse | null = null;
  loadingAsaasCharge = false;
  asaasChargeLoadError: string | null = null;
  error: string | null = null;
  /** Aviso exibido quando os dados de recebimento foram limpos (cobrança não encontrada no Asaas) */
  receiptClearedWarning: string | null = null;
  /** Histórico de pagamentos do título (GET /id/payments) */
  paymentHistory: AccountsReceivablePayment[] = [];
  loadingPayments = false;
  /** IDs dos pagamentos selecionados para cancelamento em lote */
  selectedPaymentIds = new Set<number>();
  cancellingPayments = false;
  /** Verificação de status Asaas roda só uma vez ao abrir a conta */
  private statusCheckDoneThisView = false;
  private pendingReceiveFromQuery = false;

  // Payment Modal State
  showAsaasModal = false;
  asaasBillingType: 'BOLETO' | 'CREDIT_CARD' | 'PIX' = 'BOLETO';
  creditCard = {
    holderName: '',
    number: '',
    expiryMonth: '',
    expiryYear: '',
    ccv: ''
  };

  /** Modal de recebimento manual (múltiplas formas de pagamento) */
  showReceiveModal = false;
  receiveDate = '';
  receivePayments: { paymentMethod: string; amount: number; reference?: string }[] = [];
  /** 'asaas' | 'bb_boleto' | 'bb_pix' | 'register' */
  payAction: 'asaas' | 'bb_boleto' | 'bb_pix' | 'register' = 'asaas';
  /** Opções de forma de pagamento para registrar o recebimento (Asaas, BB e outras) */
  receivePaymentMethodOptions = [
    'PIX (Asaas)',
    'Boleto (Asaas)',
    'Cartão (Asaas)',
    'PIX (Banco do Brasil)',
    'Boleto (Banco do Brasil)',
    'Dinheiro',
    'PIX',
    'Cartão de Crédito',
    'Cartão de Débito',
    'Boleto',
    'Transferência Bancária',
    'Cheque',
    'Depósito',
    'Outros'
  ];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private accountsReceivableService: AccountsReceivableService,
    private paymentService: PaymentService,
    private customersService: CustomersService,
    private errorService: ErrorService,
    private cashflowService: CashflowService,
    private authService: AuthService,
    private confirmationService: ConfirmationService,
    private serviceOrderReceivableSyncService: ServiceOrderReceivableSyncService
  ) {}

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      this.accountsReceivableId = +params['id'];
      this.receiptClearedWarning = null;
      this.loadAccountsReceivable();
    });

    this.route.queryParams.subscribe(queryParams => {
      this.pendingReceiveFromQuery = queryParams['action'] === 'receive';
      this.tryOpenReceiveFromQuery();
    });
  }

  private tryOpenReceiveFromQuery(): void {
    if (!this.pendingReceiveFromQuery || !this.accountsReceivable) return;
    this.pendingReceiveFromQuery = false;
    if (this.canReceive()) {
      this.openReceiveModal();
    }
  }

  loadAccountsReceivable(): void {
    if (!this.accountsReceivableId) {
      this.error = 'ID da conta a receber não fornecido';
      return;
    }

    this.loading = true;
    this.error = null;

    this.accountsReceivableService.getAccountsReceivableById(this.accountsReceivableId).subscribe({
      next: (accountsReceivable) => {
        this.accountsReceivable = accountsReceivable;
        this.asaasChargeId = this.resolveAsaasChargeId(accountsReceivable);
        this.loadAsaasChargeDetails();

        this.loading = false;
        if (!this.statusCheckDoneThisView) {
          this.statusCheckDoneThisView = true;
          // Só consulta Asaas automaticamente se a conta estiver vinculada a cobrança Asaas pendente
          if (this.hasChargePending() && this.isAsaasLinked(this.accountsReceivable)) {
            this.checkAsaasPaymentStatusAndUpdateAccount(true);
          }
        }
        this.loadPaymentHistory();
        this.tryOpenReceiveFromQuery();
      },
      error: (error) => {
        console.error('Erro ao carregar conta a receber:', error);
        this.error = 'Erro ao carregar dados da conta a receber';
        this.loading = false;
      }
    });
  }

  /** Carrega o histórico de pagamentos do título (GET /api/accountsreceivable/{id}/payments). */
  loadPaymentHistory(): void {
    if (!this.accountsReceivableId) return;
    this.loadingPayments = true;
    this.paymentHistory = [];
    this.selectedPaymentIds.clear();
    this.accountsReceivableService.getPaymentsByAccountId(this.accountsReceivableId).subscribe({
      next: (payments) => {
        this.paymentHistory = payments || [];
        this.loadingPayments = false;
        if (!this.asaasChargeId) {
          const fromHistory = (payments || []).find((p) =>
            (p.reference || '').trim().toLowerCase().startsWith('pay_')
          );
          if (fromHistory?.reference) {
            this.asaasChargeId = fromHistory.reference.trim();
            this.loadAsaasChargeDetails();
          }
        }
      },
      error: () => {
        this.loadingPayments = false;
      }
    });
  }

  // ===== CANCELAMENTO EM LOTE DE PAGAMENTOS =====

  /** Pagamentos já cancelados não podem ser selecionados novamente. */
  isPaymentCancelled(payment: AccountsReceivablePayment): boolean {
    return payment.status?.toLowerCase() === 'cancelado';
  }

  togglePaymentSelection(id: number): void {
    const payment = this.paymentHistory.find(p => p.id === id);
    if (!payment || this.isPaymentCancelled(payment)) return;
    if (this.selectedPaymentIds.has(id)) {
      this.selectedPaymentIds.delete(id);
    } else {
      this.selectedPaymentIds.add(id);
    }
  }

  isPaymentSelected(id: number): boolean {
    return this.selectedPaymentIds.has(id);
  }

  /** Considera apenas pagamentos não cancelados na contagem de "todos selecionados". */
  private selectablePayments(): AccountsReceivablePayment[] {
    return this.paymentHistory.filter(p => !this.isPaymentCancelled(p));
  }

  allPaymentsSelected(): boolean {
    const selectable = this.selectablePayments();
    return selectable.length > 0 && selectable.every(p => this.selectedPaymentIds.has(p.id));
  }

  toggleSelectAllPayments(): void {
    if (this.allPaymentsSelected()) {
      this.selectedPaymentIds.clear();
    } else {
      this.selectablePayments().forEach(p => this.selectedPaymentIds.add(p.id));
    }
  }

  /**
   * Pagamentos só não podem ser cancelados quando o título já está totalmente pago ("Recebido")
   * ou cancelado. Títulos vencidos, pendentes e parciais permitem cancelamento.
   */
  /** True se o vencimento do título ainda não passou (pagamento não concretizado). */
  private isDueDateFuture(): boolean {
    const ar = this.accountsReceivable;
    if (!ar?.dueDate) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dueDate = new Date(ar.dueDate);
    dueDate.setHours(0, 0, 0, 0);
    return dueDate >= today;
  }

  canCancelSelectedPayments(): boolean {
    const status = this.accountsReceivable?.status?.toLowerCase() || '';

    if (['recebido', 'cancelado'].includes(status)) return false;

    // Parcial com vencimento passado = valor já recebido de fato → bloqueia
    // Parcial com vencimento futuro = pagamento ainda não concretizado → permite
    if (['parcial', 'parcialmente recebido'].includes(status) && !this.isDueDateFuture()) return false;

    return this.selectedPaymentIds.size > 0 && !this.cancellingPayments;
  }

  /** Retorna label e classe CSS do badge de situação usando o status do próprio pagamento. */
  getPaymentStatusBadge(payment: AccountsReceivablePayment): { label: string; cssClass: string } {
    switch (payment.status?.toLowerCase()) {
      case 'confirmado':
        return { label: 'Confirmado', cssClass: 'badge bg-success' };
      case 'pendente':
        return { label: 'Pendente', cssClass: 'badge bg-secondary' };
      case 'cancelado':
        return { label: 'Cancelado', cssClass: 'badge bg-danger' };
      default:
        return { label: payment.status || 'Confirmado', cssClass: 'badge bg-success' };
    }
  }

  /** Mensagem explicando por que o cancelamento está bloqueado (usado no tooltip/alerta). */
  getCancelBlockedReason(): string {
    const status = this.accountsReceivable?.status?.toLowerCase() || '';
    if (status === 'recebido') return 'Não é possível cancelar pagamentos de um título já recebido (pago).';
    if (['parcial', 'parcialmente recebido'].includes(status) && !this.isDueDateFuture()) {
      return 'Não é possível cancelar pagamentos de um título parcialmente recebido com vencimento no passado.';
    }
    if (status === 'cancelado') return 'Este título já está cancelado.';
    return '';
  }

  /**
   * Cancela os pagamentos selecionados em duas etapas:
   * 1. Cancela cobranças Asaas (via chargeId na referência) usando cancelBoletosBatch.
   * 2. Faz soft delete dos pagamentos no banco via cancelPaymentsBatch e recalcula o título.
   */
  cancelSelectedPayments(): void {
    if (!this.canCancelSelectedPayments()) return;

    const paymentIds = Array.from(this.selectedPaymentIds);
    const count = paymentIds.length;

    const confirmMessage = `Tem certeza que deseja cancelar ${count} pagamento(s) selecionado(s)?\n\nEsta ação não pode ser desfeita. Os valores do título serão recalculados automaticamente.`;
    if (!confirm(confirmMessage)) return;

    this.cancellingPayments = true;

    const selectedPayments = this.paymentHistory.filter(p => this.selectedPaymentIds.has(p.id));
    const asaasChargeIds = selectedPayments
      .map(p => p.reference?.trim() || '')
      .filter(ref => ref.startsWith('pay_'));

    const cancelAsaas$: Observable<CancelBoletosBatchResponse | null> = asaasChargeIds.length > 0
      ? this.paymentService.cancelBoletosBatch(asaasChargeIds)
      : of(null as CancelBoletosBatchResponse | null);

    cancelAsaas$.pipe(
      switchMap((asaasResult: CancelBoletosBatchResponse | null) => {
        if (asaasResult && asaasResult.totalFailed > 0) {
          const failedIds = asaasResult.failed.map(f => f.id).join(', ');
          this.errorService.showInfoMessage(
            `Asaas: ${asaasResult.totalCancelled} cancelado(s), ${asaasResult.totalFailed} falhou (${failedIds}). Prosseguindo com cancelamento interno.`,
            8000
          );
        }
        const cancelledBy =
          this.authService.getUserNameFromToken() ||
          this.authService.currentUserValue?.username ||
          'system';
        return this.accountsReceivableService.cancelPaymentsBatch(paymentIds, cancelledBy);
      })
    ).subscribe({
      next: (result: CancelPaymentsBatchResponse) => {
        this.cancellingPayments = false;
        this.selectedPaymentIds.clear();
        this.errorService.showInfoMessage(
          `${result.cancelledPayments} pagamento(s) cancelado(s). ${result.affectedReceivables} título(s) recalculado(s).`
        );
        this.cashflowService.requestRefresh();
        this.loadAccountsReceivable();
      },
      error: () => {
        this.cancellingPayments = false;
      }
    });
  }

  formatDate(date: string | Date): string {
    if (!date) return 'N/A';
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toLocaleDateString('pt-BR');
  }

  formatCurrency(value: number): string {
    if (value === null || value === undefined) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  }

  getStatusClass(status: string): string {
    switch (status.toLowerCase()) {
      case 'pendente':
        return 'status-pending';
      case 'aprovado':
        return 'status-approved';
      case 'recebido':
        return 'status-received';
      case 'cancelado':
        return 'status-cancelled';
      case 'vencido':
        return 'status-overdue';
      case 'parcialmente recebido':
      case 'parcial':
        return 'status-partial';
      default:
        return 'status-default';
    }
  }

  isOverdue(): boolean {
    if (!this.accountsReceivable) return false;

    const status = (this.accountsReceivable.status || '').toLowerCase();
    if (status === 'vencido') return true;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const dueDate = new Date(this.accountsReceivable.dueDate);
    dueDate.setHours(0, 0, 0, 0);

    return dueDate < today && (status === 'pendente' || status === 'aprovado');
  }

  canReceive(): boolean {
    return canReceiveAccountsReceivable(this.accountsReceivable);
  }

  getReceiveBlockedReason(): string {
    return getReceiveBlockedReason(this.accountsReceivable);
  }

  /**
   * Vencimento enviado ao Asaas/BB: títulos vencidos usam a data atual (gateway não aceita data passada).
   */
  private getChargeDueDateForGateway(): string {
    const today = new Date().toISOString().split('T')[0];
    const ar = this.accountsReceivable;
    if (!ar?.dueDate) return today;
    const accountDue = this.formatDateForApi(ar.dueDate);
    if (!accountDue) return today;
    return accountDue < today ? today : accountDue;
  }

  /** True se a conta está pendente/aprovada/parcialmente recebida (útil para mensagens de UI). */
  isPendingOrPartial(): boolean {
    if (!this.accountsReceivable) return false;
    const status = this.accountsReceivable.status?.toLowerCase() || '';
    return status === 'pendente' || status === 'aprovado' || status === 'parcialmente recebido' || status === 'parcial';
  }

  /** True se a conta está vinculada a uma cobrança Asaas (método ou referência). */
  private isAsaasLinked(ar: AccountsReceivable | null): boolean {
    if (!ar) return false;
    const method = ar.receiptMethod?.toLowerCase() || '';
    const ref = ar.receiptReference?.toLowerCase() || '';
    return !!this.asaasChargeId || method.includes('asaas') || ref.startsWith('pay_');
  }

  /** ID da cobrança Asaas (ex.: pay_xxx) na conta ou no histórico. */
  resolveAsaasChargeId(ar: AccountsReceivable | null): string | null {
    if (!ar) return null;
    const ref = (ar.receiptReference || '').trim();
    if (ref.toLowerCase().startsWith('pay_')) return ref;
    const method = (ar.receiptMethod || '').toLowerCase();
    if (method.includes('asaas') && ref) return ref;
    return null;
  }

  hasAsaasChargeLinked(): boolean {
    return !!(this.asaasChargeId || this.resolveAsaasChargeId(this.accountsReceivable));
  }

  /** GET {payment}/payments/asaas/charges/{chargeId} */
  loadAsaasChargeDetails(): void {
    const chargeId = this.asaasChargeId || this.resolveAsaasChargeId(this.accountsReceivable);
    if (!chargeId) {
      this.asaasChargeDetails = null;
      this.asaasChargeLoadError = null;
      return;
    }
    this.asaasChargeId = chargeId;
    this.loadingAsaasCharge = true;
    this.asaasChargeLoadError = null;
    this.paymentService.getAsaasCharge(chargeId).subscribe({
      next: (raw) => {
        this.asaasChargeDetails = this.normalizeAsaasChargeResponse(raw);
        if (this.asaasChargeDetails.status) {
          this.asaasChargeStatus = this.asaasChargeDetails.status;
        }
        this.loadingAsaasCharge = false;
      },
      error: (err) => {
        this.asaasChargeDetails = null;
        this.loadingAsaasCharge = false;
        this.asaasChargeLoadError =
          err?.error?.message || err?.message || 'Não foi possível carregar a cobrança no Asaas.';
      }
    });
  }

  private normalizeAsaasChargeResponse(dto: AsaasCharge | AsaasPaymentResponse | Record<string, unknown>): AsaasPaymentResponse {
    const d = dto as Record<string, unknown>;
    return {
      id: (d['id'] ?? d['Id']) as string | undefined,
      status: (d['status'] ?? d['Status']) as string | undefined,
      value: Number(d['value'] ?? d['Value']) || undefined,
      billingType: (d['billingType'] ?? d['BillingType']) as string | undefined,
      dueDate: (d['dueDate'] ?? d['DueDate']) as string | undefined,
      paymentDate: (d['paymentDate'] ?? d['PaymentDate']) as string | null | undefined,
      invoiceNumber: (d['invoiceNumber'] ?? d['InvoiceNumber']) as string | null | undefined,
      invoiceUrl: (d['invoiceUrl'] ?? d['InvoiceUrl']) as string | null | undefined,
      bankSlipUrl: (d['bankSlipUrl'] ?? d['BankSlipUrl']) as string | null | undefined,
      nossoNumero: (d['nossoNumero'] ?? d['NossoNumero']) as string | null | undefined,
      externalReference: (d['externalReference'] ?? d['ExternalReference']) as string | null | undefined,
      description: (d['description'] ?? d['Description']) as string | undefined
    };
  }

  getAsaasChargeViewUrl(): string | null {
    const c = this.asaasChargeDetails;
    if (!c) return null;
    const url = (c.bankSlipUrl || c.invoiceUrl || '').trim();
    return url || null;
  }

  getAsaasChargeViewButtonLabel(): string {
    const bt = (this.asaasChargeDetails?.billingType || '').toUpperCase();
    if (bt === 'PIX') return 'Visualizar PIX / fatura Asaas';
    if (bt === 'CREDIT_CARD') return 'Visualizar fatura Asaas';
    return 'Visualizar boleto Asaas';
  }

  canViewAsaasChargeDocument(): boolean {
    return !!this.getAsaasChargeViewUrl();
  }

  viewAsaasChargeDocument(): void {
    const url = this.getAsaasChargeViewUrl();
    if (!url) {
      this.errorService.showErrorMessage('Link do boleto/cobrança não disponível no Asaas.');
      return;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  getAsaasChargeStatusLabel(): string {
    const s = this.asaasChargeDetails?.status || this.asaasChargeStatus;
    if (!s) return '—';
    const map: Record<string, string> = {
      PENDING: 'Pendente',
      RECEIVED: 'Recebido',
      CONFIRMED: 'Confirmado',
      OVERDUE: 'Vencido',
      CANCELLED: 'Cancelado'
    };
    return map[s.toUpperCase()] || s;
  }

  /** True quando há cobrança gerada (Asaas/BB) mas ainda não paga nem cancelada — valor está "em cobrança". */
  hasChargePending(): boolean {
    return hasActiveReceivableCharge(this.accountsReceivable);
  }

  editAccountsReceivable(): void {
    if (this.accountsReceivableId) {
      this.router.navigate(['/financial/accounts-receivable', this.accountsReceivableId, 'edit']);
    }
  }

  receiveAccountsReceivable(): void {
    if (this.accountsReceivableId && this.accountsReceivable) {
      this.openReceiveModal();
    }
  }

  openReceiveModal(): void {
    if (!this.accountsReceivable || !this.canReceive()) return;
    this.receiveDate = this.formatDateForApi(new Date()) || new Date().toISOString().split('T')[0];
    const amountDue = this.getAmountToReceiveInModal();
    this.receivePayments = [{ paymentMethod: '', amount: amountDue }];
    this.payAction = 'asaas';
    this.showReceiveModal = true;
  }

  closeReceiveModal(): void {
    this.showReceiveModal = false;
    this.receivePayments = [];
  }

  addReceivePaymentRow(): void {
    const balance = Math.max(0, this.getReceiveBalance());
    this.receivePayments = [...this.receivePayments, { paymentMethod: '', amount: balance }];
  }

  removeReceivePaymentRow(index: number): void {
    if (this.receivePayments.length <= 1) return;
    this.receivePayments = this.receivePayments.filter((_, i) => i !== index);
  }

  getReceiveTotal(): number {
    return this.receivePayments.reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
  }

  /** Valor ainda a receber nesta conta (considera recebimento parcial). Usado no modal Receber. */
  getAmountToReceiveInModal(): number {
    if (!this.accountsReceivable) return 0;
    const ar = this.accountsReceivable;
    const toReceive = Number(ar.amountToReceive) || 0;
    const received = Number(ar.amountReceived) || 0;
    const remaining = ar.remainingAmount;
    if (remaining != null && remaining !== undefined && !isNaN(Number(remaining))) {
      return Math.max(0, Math.round(Number(remaining) * 100) / 100);
    }
    return Math.max(0, Math.round((toReceive - received) * 100) / 100);
  }

  getReceiveBalance(): number {
    return this.getAmountToReceiveInModal() - this.getReceiveTotal();
  }

  canConfirmReceive(): boolean {
    if (!this.accountsReceivable) return false;
    const valid = this.receivePayments.filter(p => (p.paymentMethod?.trim() || '') !== '' && (Number(p.amount) || 0) > 0);
    return valid.length >= 1;
  }

  confirmReceive(): void {
    const ar = this.accountsReceivable;
    if (!ar?.id || !this.canConfirmReceive()) return;

    const validPayments = this.receivePayments
      .filter(p => (p.paymentMethod?.trim() || '') !== '' && (Number(p.amount) || 0) > 0)
      .map(p => ({
        paymentMethod: p.paymentMethod.trim(),
        amount: Number(p.amount),
        reference: p.reference?.trim() || undefined
      }));

    const paymentDate = this.receiveDate?.trim()
      ? new Date(this.receiveDate + 'T12:00:00').toISOString()
      : new Date().toISOString();

    this.paymentLoading = true;
    const requests = validPayments.map(p =>
      this.accountsReceivableService.registerPayment({
        accountsReceivableId: ar.id,
        amount: p.amount,
        paymentMethod: p.paymentMethod,
        paymentDate,
        reference: p.reference
      })
    );

    forkJoin(requests).subscribe({
      next: () => {
        // Após registrar o(s) pagamento(s), atualiza o título para refletir amountReceived/status
        const receivedNow = validPayments.reduce((s, p) => s + (Number(p.amount) || 0), 0);
        const previousReceived = ar.amountReceived ?? 0;
        const totalReceivedAfterThis = previousReceived + receivedNow;
        const amountToReceive = ar.amountToReceive ?? 0;
        const status = totalReceivedAfterThis >= amountToReceive - 0.009 ? 'Recebido' : 'Parcial';

        const receiptMethod = validPayments.length > 1
          ? `Misto (${validPayments.map(p => p.paymentMethod).join('; ')})`
          : validPayments[0]?.paymentMethod;

        const update = this.buildAccountsReceivableUpdatePayload(ar, {
          status,
          amountReceived: totalReceivedAfterThis,
          receiptDate: this.receiveDate?.trim() ? this.receiveDate.trim() : this.formatDateForApi(new Date()),
          receiptMethod
        });

        this.accountsReceivableService.updateAccountsReceivable(update).subscribe({
          next: () => {
            this.paymentLoading = false;
            this.closeReceiveModal();
            this.errorService.showInfoMessage(
              status === 'Recebido' ? 'Recebimento registrado com sucesso.' : 'Recebimento parcial registrado.'
            );
            this.cashflowService.requestRefresh();
            this.syncServiceOrderPaymentIfApplicable(
              { ...ar, status, amountReceived: totalReceivedAfterThis },
              status
            );
            this.loadAccountsReceivable();
          },
          error: (err) => {
            this.paymentLoading = false;
            const msg = err?.error?.message || err?.message || 'Pagamentos registrados, mas houve erro ao atualizar o título.';
            this.errorService.showErrorMessage(msg);
            this.closeReceiveModal();
            this.loadAccountsReceivable();
          }
        });
      },
      error: (err) => {
        this.paymentLoading = false;
        const msg = err?.error?.message || err?.message || 'Erro ao registrar recebimento.';
        this.errorService.showErrorMessage(msg);
      }
    });
  }

  /**
   * Monta o payload de atualização para marcar como recebido (total ou parcial).
   * Uma forma: envia receiptMethod + amountReceived (sem receiptPayments).
   * Duas ou mais: envia receiptPayments com a soma igual a amountReceived.
   */
  private buildReceivePayload(
    ar: AccountsReceivable,
    amountReceived: number,
    status: string,
    validPayments: ReceiptPayment[]
  ): AccountsReceivableUpdate {
    const base = this.buildAccountsReceivableUpdatePayload(ar, {
      status,
      amountReceived,
      receiptDate: this.receiveDate || this.formatDateForApi(new Date()),
      receiptMethod: validPayments.length === 1 ? validPayments[0].paymentMethod : undefined,
      receiptReference: ar.receiptReference,
      bankSlipNumber: ar.bankSlipNumber
    });
    if (validPayments.length > 1) {
      base.receiptPayments = validPayments;
      base.receiptMethod = validPayments.map(p => p.paymentMethod).join('; ');
    } else if (validPayments.length === 1) {
      base.receiptMethod = validPayments[0].paymentMethod;
    }
    return base;
  }

  /** Para gerar (Asaas/BB) exige ao menos uma forma preenchida; para registrar idem (sem exiger saldo zero). */
  canConfirmPayAction(): boolean {
    if (!this.accountsReceivable) return false;
    const valid = this.receivePayments.filter(p => (p.paymentMethod?.trim() || '') !== '' && (Number(p.amount) || 0) > 0);
    return valid.length >= 1;
  }

  getPayActionButtonLabel(): string {
    switch (this.payAction) {
      case 'asaas': return 'Gerar via Asaas';
      case 'bb_boleto': return 'Gerar Boleto BB';
      case 'bb_pix': return 'Gerar PIX BB';
      case 'register': return 'Confirmar recebimento';
      default: return 'Confirmar';
    }
  }

  confirmPayAction(): void {
    if (!this.canConfirmPayAction() || this.paymentLoading) return;
    if (this.payAction === 'register') {
      this.confirmReceive();
      return;
    }
    if (this.payAction === 'asaas') {
      this.applyAsaasBillingTypeFromPayments();
      this.closeReceiveModal();
      this.openAsaasModal();
      return;
    }
    this.closeReceiveModal();
    if (this.payAction === 'bb_boleto') {
      this.generateBBBoleto();
      return;
    }
    if (this.payAction === 'bb_pix') {
      this.generateBBPix();
      return;
    }
  }

  /** Define o tipo de cobrança Asaas a partir da primeira forma selecionada no modal Pagar (evita selecionar de novo). */
  private applyAsaasBillingTypeFromPayments(): void {
    const first = this.receivePayments.find(p => (p.paymentMethod?.trim() || '') !== '' && (Number(p.amount) || 0) > 0);
    if (!first?.paymentMethod) return;
    const m = first.paymentMethod.trim().toLowerCase();
    if (m.includes('pix') && m.includes('asaas')) this.asaasBillingType = 'PIX';
    else if (m.includes('boleto') && m.includes('asaas')) this.asaasBillingType = 'BOLETO';
    else if ((m.includes('cartão') || m.includes('cartao') || m.includes('crédito') || m.includes('credito')) && m.includes('asaas')) this.asaasBillingType = 'CREDIT_CARD';
    else if (m === 'pix') this.asaasBillingType = 'PIX';
    else if (m.includes('boleto')) this.asaasBillingType = 'BOLETO';
    else if (m.includes('cartão') || m.includes('cartao') || m.includes('crédito') || m.includes('credito')) this.asaasBillingType = 'CREDIT_CARD';
  }

  deleteAccountsReceivable(): void {
    if (!this.accountsReceivable || !this.accountsReceivableId) return;

    const doc = this.accountsReceivable.documentNumber || String(this.accountsReceivableId);
    this.confirmationService
      .confirmDelete(doc, 'a conta a receber')
      .pipe(take(1))
      .subscribe((confirmed) => {
        if (!confirmed) return;
        this.accountsReceivableService.deleteAccountsReceivable(this.accountsReceivableId!).subscribe({
          next: () => this.router.navigate(['/financial/accounts-receivable']),
          error: (error) => {
            console.error('Erro ao excluir conta a receber:', error);
            this.error = 'Erro ao excluir conta a receber';
          }
        });
      });
  }

  goBack(): void {
    this.router.navigate(['/financial/accounts-receivable']);
  }

  // Payment Methods

  openAsaasModal(): void {
    if (!this.accountsReceivable || !this.accountsReceivable.customerId) {
      this.errorService.showErrorMessage('Conta a receber sem cliente vinculado.');
      return;
    }
    this.showAsaasModal = true;
    this.asaasBillingType = 'BOLETO'; // Reset to default
    // Reset credit card data
    this.creditCard = {
        holderName: '',
        number: '',
        expiryMonth: '',
        expiryYear: '',
        ccv: ''
    };
  }

  closeAsaasModal(): void {
    this.showAsaasModal = false;
  }

  confirmAsaasPayment(): void {
    this.generateAsaasCharge();
    this.closeAsaasModal();
  }

  generateAsaasCharge(): void {
    if (!this.accountsReceivable || !this.accountsReceivable.customerId) {
      this.errorService.showErrorMessage('Conta a receber sem cliente vinculado.');
      return;
    }

    this.paymentLoading = true;

    const ar = this.accountsReceivable;
    const linkedCustomer = ar.customer;

    // Usar customer vinculado quando já vier na conta a receber; senão buscar por ID
    if (linkedCustomer?.documentNumber) {
      this.buildAndCreateAsaasCharge(linkedCustomer);
      return;
    }

    this.customersService.getById(ar.customerId!).subscribe({
      next: (customer) => {
        if (!customer.documentNumber) {
          this.paymentLoading = false;
          this.errorService.showErrorMessage('Cliente sem CPF/CNPJ cadastrado.');
          return;
        }
        this.buildAndCreateAsaasCharge(customer);
      },
      error: () => {
        this.paymentLoading = false;
      }
    });
  }

  /**
   * Monta AsaasCustomer a partir do cliente vinculado (Customer ou objeto expandido da API com addresses/contacts).
   * Se o cliente não existir no Asaas, createAsaasChargeWithCustomerValidation cria usando estes dados.
   */
  private buildAsaasCustomerFromLinkedCustomer(c: Customer | Partial<Customer> & { name: string; documentNumber: string }): AsaasCustomer {
    const addr = (c as any).addresses?.[0] || (c as any).Addresses?.[0];
    const contact = (c as any).contacts?.[0] || (c as any).Contacts?.[0];
    const zipCode = c.zipCode ?? addr?.zipCode ?? addr?.ZipCode;
    const number = c.number ?? addr?.number ?? addr?.Number;
    const complement = c.complement ?? addr?.complement ?? addr?.Complement;
    const primaryPhone = c.primaryPhone ?? contact?.phoneNumber ?? contact?.PhoneNumber;
    const secondaryPhone = c.secondaryPhone ?? contact?.secondaryPhoneNumber ?? contact?.SecondaryPhoneNumber;
    const email = c.email ?? contact?.email ?? contact?.Email;

    return {
      name: c.name,
      cpfCnpj: c.documentNumber,
      email,
      mobilePhone: primaryPhone,
      phone: secondaryPhone,
      postalCode: zipCode,
      addressNumber: number,
      addressComplement: complement
    };
  }

  private buildAndCreateAsaasCharge(customer: Customer | (Partial<Customer> & { name: string; documentNumber: string })): void {
    const asaasCustomer = this.buildAsaasCustomerFromLinkedCustomer(customer);

    const chargeData: Omit<AsaasCharge, 'customerId'> = {
      billingType: this.asaasBillingType,
      value: this.getAmountToReceiveInModal(),
      dueDate: this.getChargeDueDateForGateway(),
      description: `Pagamento Ref: ${this.accountsReceivable!.documentNumber}`,
      externalReference: this.accountsReceivable!.documentNumber
    };

    if (this.asaasBillingType === 'CREDIT_CARD') {
      chargeData.creditCard = {
        holderName: this.creditCard.holderName,
        number: this.creditCard.number,
        expiryMonth: this.creditCard.expiryMonth,
        expiryYear: this.creditCard.expiryYear,
        ccv: this.creditCard.ccv
      };
      const contact = (customer as any).contacts?.[0] || (customer as any).Contacts?.[0];
      const phone = customer.primaryPhone ?? customer.secondaryPhone ?? contact?.phoneNumber ?? contact?.PhoneNumber ?? '0000000000';
      const email = customer.email ?? (customer as any).email ?? contact?.email ?? contact?.Email ?? '';
      const addr = (customer as any).addresses?.[0] || (customer as any).Addresses?.[0];
      chargeData.creditCardHolderInfo = {
        name: customer.name,
        email: email || '',
        cpfCnpj: customer.documentNumber,
        postalCode: customer.zipCode ?? addr?.zipCode ?? addr?.ZipCode ?? '',
        addressNumber: customer.number ?? addr?.number ?? addr?.Number ?? '',
        addressComplement: customer.complement ?? addr?.complement ?? addr?.Complement,
        phone,
        mobilePhone: customer.primaryPhone ?? contact?.phoneNumber ?? contact?.PhoneNumber
      };
    }

    this.paymentService.createAsaasChargeWithCustomerValidation(asaasCustomer, chargeData).subscribe({
      next: (res) => {
        this.paymentLoading = false;
        this.errorService.showInfoMessage('Cobrança Asaas gerada com sucesso!');
        if (res.invoiceUrl || res.bankSlipUrl) {
          window.open(res.bankSlipUrl || res.invoiceUrl, '_blank');
        }
        this.updateAccountWithAsaasCreateResponse(res);
      },
      error: () => {
        this.paymentLoading = false;
      }
    });
  }

  /**
   * Após criar o pagamento no Asaas, atualiza a conta a receber com os dados retornados
   * (forma de recebimento, referência, número do boleto). Status e data/valor de recebimento
   * só são preenchidos quando o pagamento for confirmado (verificar status).
   * 
   * IMPORTANTE: O campo 'id' retornado pelo Asaas (ex: "pay_fgmcmdyylbtp3t7n") é essencial
   * para operações futuras como cancelamento. Ele é salvo no 'receiptReference'.
   */
  private updateAccountWithAsaasCreateResponse(payment: AsaasCharge | AsaasPaymentResponse): void {
    const receiptMethod = this.mapAsaasBillingTypeToReceiptMethod(
      (payment as AsaasPaymentResponse).billingType ?? (payment as AsaasCharge).billingType
    );
    
    // PRIORIDADE: Usar o ID da cobrança Asaas (ex: "pay_fgmcmdyylbtp3t7n")
    // Este ID é necessário para cancelar a cobrança posteriormente
    const asaasChargeId = (payment as AsaasPaymentResponse).id ?? (payment as AsaasCharge).id ?? '';
    
    const receiptReference = asaasChargeId 
      || (payment as AsaasPaymentResponse).invoiceNumber 
      || (payment as AsaasCharge).externalReference 
      || '';
    
    const bankSlipNumber =
      (payment as AsaasPaymentResponse).nossoNumero ??
      (payment as AsaasPaymentResponse).invoiceNumber ??
      undefined;
    
    // Armazena o ID da cobrança para uso futuro (cancelamento)
    if (asaasChargeId) {
      this.asaasChargeId = asaasChargeId;
    }
    
    this.updateAccountWithPaymentDetails(receiptMethod, receiptReference, bankSlipNumber);
  }

  /**
   * Consulta o status da cobrança no Asaas (GET charges?externalReference=numeroDoDocumento) e, se pago, atualiza a conta.
   * Chamado automaticamente ao visualizar a conta (silent=true) e ao clicar em "Verificar status no Asaas" (silent=false).
   */
  checkAsaasPaymentStatusAndUpdateAccount(silent = false): void {
    const ar = this.accountsReceivable;
    if (!ar?.id) {
      if (!silent) this.errorService.showErrorMessage('Conta a receber inválida.');
      return;
    }

    if (!silent) {
      this.checkingPaymentStatus = true;
      this.asaasChargeStatus = null;
    }

    const filters: { limit: number; invoiceNumber?: string; externalReference?: string } = { limit: 10 };
    if (ar.documentNumber?.trim()) {
      filters.externalReference = ar.documentNumber.trim();
    } else if (ar.receiptReference?.trim()) {
      filters.invoiceNumber = ar.receiptReference.trim();
    } else {
      if (!silent) {
        this.checkingPaymentStatus = false;
        this.errorService.showInfoMessage('Número do documento é obrigatório para consultar status no Asaas.');
      }
      return;
    }

    this.paymentService.listAsaasCharges(filters).subscribe({
      next: (response) => {
        if (!silent) this.checkingPaymentStatus = false;
        const list = response?.data ?? [];
        const payments: AsaasPaymentResponse[] = Array.isArray(list) ? list : [];
        const paidStatuses = ['RECEIVED', 'CONFIRMED', 'RECEBIDO', 'CONFIRMADO'];
        const isPaid = (p: AsaasPaymentResponse) => p.status && paidStatuses.includes(p.status.toUpperCase());
        const matchesAccount = (p: AsaasPaymentResponse) =>
          (ar.documentNumber && p.externalReference === ar.documentNumber) ||
          (ar.receiptReference && p.invoiceNumber === ar.receiptReference);
        const paidPayment = payments.find((p: AsaasPaymentResponse) => isPaid(p) && matchesAccount(p))
          ?? payments.find((p: AsaasPaymentResponse) => isPaid(p));
        const lastPayment = payments.find((p: AsaasPaymentResponse) => matchesAccount(p)) ?? payments[0];

        if (lastPayment) {
          this.asaasChargeStatus = lastPayment.status ?? 'Desconhecido';
          if (lastPayment.id) {
            this.asaasChargeId = lastPayment.id;
            this.loadAsaasChargeDetails();
          }
        }

        if (paidPayment) {
          const receiptDetails = this.mapAsaasPaymentToReceiptDetails(paidPayment, ar);
          const update = this.buildAccountsReceivableUpdatePayload(ar, receiptDetails);
          this.accountsReceivableService.updateAccountsReceivable(update).subscribe({
            next: () => {
              this.errorService.showInfoMessage('Pagamento confirmado no Asaas. Detalhes de recebimento atualizados. O valor foi lançado no fluxo de caixa.');
              this.cashflowService.requestRefresh();
              this.syncServiceOrderPaymentIfApplicable(
                { ...ar, ...receiptDetails },
                receiptDetails.status
              );
              this.loadAccountsReceivable();
            },
            error: (err) => {
              if (!silent) this.errorService.showErrorMessage(err?.error?.message || 'Erro ao atualizar conta a receber.');
            }
          });
        } else if (payments.length === 0 && this.accountHasReceiptData(ar) && this.isAsaasLinked(ar)) {
          const update = this.buildClearReceiptPayload(ar);
          this.accountsReceivableService.updateAccountsReceivable(update).subscribe({
            next: () => {
              this.receiptClearedWarning = 'Nenhuma cobrança encontrada no Asaas para este documento. Os dados de recebimento foram limpos. Será necessário gerar outra cobrança se quiser receber.';
              this.loadAccountsReceivable();
              this.errorService.showInfoMessage(this.receiptClearedWarning, 20000);
            },
            error: (err) => {
              if (!silent) this.errorService.showErrorMessage(err?.error?.message || 'Erro ao atualizar conta.');
            }
          });
        } else if (!silent && payments.length > 0) {
          this.errorService.showInfoMessage(`Status no Asaas: ${this.asaasChargeStatus || 'Pendente'}. A conta será atualizada quando o pagamento for confirmado.`);
        }
      },
      error: () => {
        if (!silent) {
          this.checkingPaymentStatus = false;
          this.errorService.showErrorMessage('Erro ao consultar status no Asaas.');
        }
      }
    });
  }

  /**
   * Mapeia o objeto de pagamento retornado pela API Asaas para os campos de detalhe de recebimento da conta.
   * Usa os dados que só existem após a criação/confirmação do pagamento (paymentDate, value, billingType, etc.).
   */
  private syncServiceOrderPaymentIfApplicable(ar: AccountsReceivable, status: string): void {
    if (status !== 'Recebido') {
      return;
    }

    this.serviceOrderReceivableSyncService.syncWorkSectionPaymentAfterReceivable(ar).subscribe();
  }

  private mapAsaasPaymentToReceiptDetails(
    payment: AsaasPaymentResponse,
    ar: AccountsReceivable
  ): {
    status: string;
    amountReceived: number;
    receiptDate: string;
    receiptMethod?: string;
    receiptReference?: string;
    bankSlipNumber?: string;
  } {
    const receiptDate =
      (payment.clientPaymentDate || payment.paymentDate)
        ?.toString()
        ?.split('T')[0] ||
      new Date().toISOString().split('T')[0];
    const paymentValue = payment.value ?? ar.amountToReceive ?? 0;
    const previousReceived = ar.amountReceived ?? 0;
    const totalReceived = previousReceived + paymentValue;
    const amountToReceive = ar.amountToReceive ?? 0;
    const status = totalReceived >= amountToReceive - 0.009 ? 'Recebido' : 'Parcial';
    const receiptMethod = this.mapAsaasBillingTypeToReceiptMethod(payment.billingType);
    const receiptReference = payment.invoiceNumber ?? payment.id ?? payment.externalReference ?? undefined;
    const bankSlipNumber = payment.nossoNumero ?? payment.invoiceNumber ?? undefined;

    return {
      status,
      amountReceived: totalReceived,
      receiptDate,
      receiptMethod,
      receiptReference,
      bankSlipNumber
    };
  }

  /** Usa as mesmas opções do form de cadastro (Asaas e BB) para consistência */
  private mapAsaasBillingTypeToReceiptMethod(billingType?: string | null): string {
    if (!billingType) return 'PIX (Asaas)';
    const t = billingType.toUpperCase();
    if (t === 'PIX') return 'PIX (Asaas)';
    if (t === 'BOLETO') return 'Boleto (Asaas)';
    if (t === 'CREDIT_CARD' || t === 'DEBIT_CARD') return 'Cartão (Asaas)';
    return billingType;
  }

  private formatDateForApi(date: Date | string | undefined): string {
    if (!date) return '';
    if (typeof date === 'string') return date.includes('T') ? date.split('T')[0] : date;
    return (date as Date).toISOString?.().split('T')[0] ?? '';
  }

  private buildAccountsReceivableUpdatePayload(
    ar: AccountsReceivable,
    receipt: {
      status: string;
      amountReceived: number;
      receiptDate?: string;
      receiptMethod?: string;
      receiptReference?: string;
      bankSlipNumber?: string;
    }
  ): AccountsReceivableUpdate {
    const payload: AccountsReceivableUpdate = {
      id: ar.id,
      documentNumber: ar.documentNumber,
      documentType: ar.documentType,
      issueDate: this.formatDateForApi(ar.issueDate),
      dueDate: this.formatDateForApi(ar.dueDate),
      originalAmount: ar.originalAmount,
      discountAmount: ar.discountAmount,
      interestAmount: ar.interestAmount,
      fineAmount: ar.fineAmount,
      amountToReceive: ar.amountToReceive,
      amountReceived: receipt.amountReceived,
      status: receipt.status,
      category: ar.category,
      receiptMethod: receipt.receiptMethod ?? ar.receiptMethod,
      receiptReference: receipt.receiptReference ?? ar.receiptReference,
      customerId: ar.customerId,
      costCenterId: ar.costCenterId,
      issuedByStaffId: ar.issuedByStaffId,
      receivedByStaffId: ar.receivedByStaffId,
      description: ar.description,
      invoiceNumber: ar.invoiceNumber,
      saleOrderNumber: ar.saleOrderNumber,
      installmentNumber: ar.installmentNumber,
      totalInstallments: ar.totalInstallments,
      bankSlipNumber: receipt.bankSlipNumber ?? ar.bankSlipNumber,
      checkNumber: ar.checkNumber
    };
    if (receipt.receiptDate && receipt.receiptDate.trim() !== '') {
      payload.receiptDate = receipt.receiptDate;
    }
    return payload;
  }

  /** Indica se a conta tem dados de recebimento preenchidos (cobrança já foi gerada ou paga). */
  private accountHasReceiptData(ar: AccountsReceivable): boolean {
    const hasReceiptFields = !!(ar.receiptReference?.trim() || ar.receiptMethod?.trim() || ar.bankSlipNumber?.trim());
    const hasAmountReceived = ar.amountReceived != null && Number(ar.amountReceived) > 0;
    const isReceivedStatus = ar.status && !['pendente', 'vencido'].includes(ar.status.toLowerCase());
    return hasReceiptFields || hasAmountReceived || !!isReceivedStatus;
  }

  /** Monta payload para limpar dados de recebimento quando a cobrança não existe mais no Asaas. */
  private buildClearReceiptPayload(ar: AccountsReceivable): AccountsReceivableUpdate {
    const base: AccountsReceivableUpdate = {
      id: ar.id,
      documentNumber: ar.documentNumber,
      documentType: ar.documentType,
      issueDate: this.formatDateForApi(ar.issueDate),
      dueDate: this.formatDateForApi(ar.dueDate),
      originalAmount: ar.originalAmount,
      discountAmount: ar.discountAmount,
      interestAmount: ar.interestAmount,
      fineAmount: ar.fineAmount,
      amountToReceive: ar.amountToReceive,
      amountReceived: 0,
      status: 'Pendente',
      category: ar.category,
      customerId: ar.customerId,
      costCenterId: ar.costCenterId,
      issuedByStaffId: ar.issuedByStaffId,
      receivedByStaffId: ar.receivedByStaffId,
      description: ar.description,
      invoiceNumber: ar.invoiceNumber,
      saleOrderNumber: ar.saleOrderNumber,
      installmentNumber: ar.installmentNumber,
      totalInstallments: ar.totalInstallments,
      checkNumber: ar.checkNumber
    };
    base.receiptMethod = '';
    base.receiptReference = '';
    base.bankSlipNumber = '';
    return base;
  }

  generateBBBoleto(): void {
    if (!this.accountsReceivable || !this.accountsReceivable.customerId) {
        this.errorService.showErrorMessage('Conta a receber sem cliente vinculado.');
        return;
    }
    
    this.paymentLoading = true;
    this.customersService.getById(this.accountsReceivable.customerId).subscribe({
        next: (customer) => {
            const boleto: BBBoleto = {
                nossoNumero: this.accountsReceivable!.documentNumber.replace(/[^0-9]/g, '').substring(0, 10), // Example logic
                valor: this.getAmountToReceiveInModal(),
                sacadoNome: customer.name,
                sacadoDocumento: customer.documentNumber,
                sacadoEndereco: `${customer.street}, ${customer.number} - ${customer.city}/${customer.state}`,
                descricao: this.accountsReceivable!.description || 'Pagamento Flit Core'
            };

            this.paymentService.createBBBoleto(boleto).subscribe({
                next: (res) => {
                    this.paymentLoading = false;
                    this.errorService.showInfoMessage('Boleto BB gerado com sucesso!');
                    const nossoNumero = (res?.nossoNumero ?? boleto.nossoNumero) || '';
                    this.updateAccountWithPaymentDetails('Boleto (Banco do Brasil)', nossoNumero, nossoNumero);
                },
                error: () => this.paymentLoading = false
            });
        },
        error: () => this.paymentLoading = false
    });
  }

  generateBBPix(): void {
    if (!this.accountsReceivable) return;
    
    this.paymentLoading = true;
    const pix: BBPix = {
        txId: this.accountsReceivable.documentNumber.replace(/[^a-zA-Z0-9]/g, '').substring(0, 25), // Needs valid txId
        valor: this.getAmountToReceiveInModal(),
        descricao: `Pagamento ${this.accountsReceivable.documentNumber}`
    };

    this.paymentService.createBBPix(pix).subscribe({
        next: (res) => {
            this.paymentLoading = false;
            this.errorService.showInfoMessage('Pix BB gerado com sucesso!');
            const txId = (res?.txId ?? pix.txId) || '';
            this.updateAccountWithPaymentDetails('PIX (Banco do Brasil)', txId, undefined);
        },
        error: () => this.paymentLoading = false
    });
  }

  /**
   * Atualiza a conta a receber com detalhes do pagamento criado (Asaas ou BB).
   * Usado após criar cobrança para preencher automaticamente forma, referência e número do boleto.
   */
  private updateAccountWithPaymentDetails(
    receiptMethod: string,
    receiptReference: string,
    bankSlipNumber?: string
  ): void {
    const ar = this.accountsReceivable;
    if (!ar?.id) return;

    const update = this.buildAccountsReceivableUpdatePayload(ar, {
      status: ar.status,
      amountReceived: ar.amountReceived ?? 0,
      receiptDate: this.formatDateForApi(ar.receiptDate) || undefined,
      receiptMethod: receiptMethod || ar.receiptMethod,
      receiptReference: receiptReference || ar.receiptReference,
      bankSlipNumber: bankSlipNumber ?? ar.bankSlipNumber
    });
    this.accountsReceivableService.updateAccountsReceivable(update).subscribe({
      next: () => this.loadAccountsReceivable(),
      error: () => { /* cobrança já foi criada */ }
    });
  }

  /**
   * Cancela uma cobrança no Asaas e atualiza a conta a receber
   * 
   * Usa o ID da cobrança Asaas (ex: "pay_fgmcmdyylbtp3t7n") que foi salvo
   * no campo 'receiptReference' quando a cobrança foi criada.
   */
  cancelAsaasCharge(): void {
    const ar = this.accountsReceivable;
    if (!ar?.id) {
      this.errorService.showErrorMessage('Conta a receber inválida.');
      return;
    }

    // PRIORIDADE: usar o ID da cobrança Asaas salvo no 'receiptReference'
    // Formato esperado: "pay_fgmcmdyylbtp3t7n" ou similar
    const chargeId = ar.receiptReference || this.asaasChargeId;

    if (!chargeId || chargeId.trim() === '') {
      this.errorService.showErrorMessage('Nenhuma cobrança Asaas vinculada a esta conta. O ID da cobrança não foi encontrado.');
      return;
    }

    // Validação: verificar se o ID parece ser um ID Asaas válido
    if (!chargeId.startsWith('pay_') && !chargeId.startsWith('cus_')) {
      if (!confirm(`O ID da cobrança (${chargeId}) não parece ser um ID Asaas válido. Deseja continuar mesmo assim?`)) {
        return;
      }
    }

    const confirmMessage = `Tem certeza que deseja cancelar a cobrança Asaas?\n\nID da Cobrança: ${chargeId}\n\nEsta ação não pode ser desfeita e o boleto/link de pagamento será invalidado.`;
    
    if (!confirm(confirmMessage)) {
      return;
    }

    this.cancellingCharge = true;

    this.paymentService.cancelAsaasCharge(chargeId).subscribe({
      next: (_cancelledCharge: unknown) => {
        this.cancellingCharge = false;
        this.errorService.showInfoMessage('Cobrança cancelada com sucesso!');
        
        // Limpa dados da cobrança e mantém conta como Pendente para permitir gerar outro pagamento
        const update = this.buildClearReceiptPayload(ar);
        
        this.accountsReceivableService.updateAccountsReceivable(update).subscribe({
          next: () => {
            this.asaasChargeStatus = 'CANCELLED';
            this.loadAccountsReceivable();
          },
          error: (err: unknown) => {
            this.errorService.showErrorMessage(
              (err as any)?.error?.message || 'Cobrança cancelada, mas houve erro ao atualizar a conta.'
            );
            this.loadAccountsReceivable();
          }
        });
      },
      error: (err: unknown) => {
        this.cancellingCharge = false;
        const errorMessage = (err as any)?.error?.message || 'Erro ao cancelar cobrança no Asaas.';
        
        if ((err as any)?.status === 404) {
          this.errorService.showErrorMessage('Cobrança não encontrada no Asaas. Ela pode já ter sido cancelada.');
        } else if ((err as any)?.status === 400) {
          this.errorService.showErrorMessage('Esta cobrança não pode ser cancelada (pode já estar paga ou cancelada).');
        } else {
          this.errorService.showErrorMessage(errorMessage);
        }
      }
    });
  }

  /**
   * Verifica se a cobrança Asaas pode ser cancelada
   */
  canCancelAsaasCharge(): boolean {
    const ar = this.accountsReceivable;
    if (!ar) return false;

    // Verifica se tem referência de cobrança Asaas
    const hasChargeReference = !!(this.asaasChargeId || ar.receiptReference);
    if (!hasChargeReference) return false;

    // Verifica se a forma de recebimento é do Asaas
    const isAsaasPayment = ar.receiptMethod?.toLowerCase().includes('asaas') || false;
    if (!isAsaasPayment) return false;

    // Não pode cancelar se já foi recebido
    const isReceived = ar.status?.toLowerCase() === 'recebido';
    if (isReceived) return false;

    // Não pode cancelar se já está cancelado
    const isCancelled = ar.status?.toLowerCase() === 'cancelado';
    if (isCancelled) return false;

    // Verifica o status no Asaas (se disponível em memória)
    if (this.asaasChargeStatus) {
      const status = this.asaasChargeStatus.toUpperCase();
      if (['RECEIVED', 'CONFIRMED', 'CANCELLED'].includes(status)) {
        return false;
      }
    }

    return true;
  }

  /**
   * Descrição da cobrança para o botão cancelar (tipo e data).
   */
  getCancelChargeDescription(): string {
    const ar = this.accountsReceivable;
    if (!ar) return '';

    const parts: string[] = [];

    if (ar.receiptMethod) {
      const method = ar.receiptMethod.replace(/^asaas\s+/i, '').trim() || ar.receiptMethod;
      parts.push(method);
    }

    const date = ar.dueDate || ar.receiptDate || ar.issueDate;
    if (date) {
      const d = typeof date === 'string' ? new Date(date) : date;
      if (!isNaN(d.getTime())) {
        parts.push('venc. ' + d.toLocaleDateString('pt-BR'));
      }
    }

    return parts.length ? parts.join(', ') : '';
  }
}
